import { apiFetch } from './http';

// ── Types ──

export type DemoStatus = 'provisioning' | 'deploying' | 'live' | 'anonymizing' | 'expiring' | 'expired' | 'extended' | 'teardown' | 'archived' | 'failed';
export type DemoType = 'customer' | 'showcase';
export type DataClassification = 'customer_data' | 'anonymized' | 'synthetic' | 'public';
export type DemoHostingType = 'static' | 'fullstack';
export type AuthType = 'none' | 'password' | 'cognito';

export interface EnvVarConfig {
  placeholder: string;
  value: string;
  containsCustomerData?: boolean;
  anonymizedValue?: string;
}

export interface ApiProxyConfig {
  apiUrl: string;
  pathPattern?: string;
}

export interface DemoInstance {
  demoId: string;
  slug: string;
  name: string;
  description: string;
  productId: string;
  demoType: DemoType;
  dataClassification: DataClassification;
  hostingType: DemoHostingType;
  status: DemoStatus;
  url: string;
  s3Bucket: string;
  cfDistributionId: string;
  authType: AuthType;
  apiProxy?: ApiProxyConfig;
  envVars: EnvVarConfig[];
  currentVersion: string;
  deployCount: number;
  lastDeployAt: string;
  sourceTemplate: string;
  ttlDays: number;
  expiresAt: string;
  extendedCount: number;
  tenantId?: string;
  customerName?: string;
  engagementId?: string;
  anonymizedAt?: string;
  derivedFromDemoId?: string;
  createdBy: string;
  assignedTo?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  // Enriched fields from list
  hoursRemaining?: number;
  isExpiring?: boolean;
  isExpired?: boolean;
}

// ── API Client ──


export async function fetchDemos(filters?: { type?: string; status?: string; productId?: string }): Promise<{ demos: DemoInstance[] }> {
  const params = new URLSearchParams();
  if (filters?.type) params.set('type', filters.type);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.productId) params.set('productId', filters.productId);
  const qs = params.toString();
  return apiFetch(`/ops/demos${qs ? `?${qs}` : ''}`);
}

export async function fetchShowcaseCatalog(): Promise<{ catalog: DemoInstance[] }> {
  return apiFetch('/ops/demos/catalog');
}

export async function fetchDemo(demoId: string): Promise<DemoInstance & { deployHistory: unknown[] }> {
  return apiFetch(`/ops/demos/${demoId}`);
}

export async function createDemo(input: {
  slug: string; name: string; description: string; productId: string;
  demoType: DemoType; dataClassification: DataClassification;
  hostingType: DemoHostingType; sourceTemplate: string;
  authType?: AuthType; authPassword?: string;
  apiProxy?: ApiProxyConfig; envVars?: EnvVarConfig[];
  ttlDays?: number; tenantId?: string; customerName?: string;
  engagementId?: string; tags?: string[];
}): Promise<DemoInstance> {
  return apiFetch('/ops/demos', { method: 'POST', body: JSON.stringify(input) });
}

export async function deployDemo(demoId: string, input: { source?: string; version?: string }): Promise<unknown> {
  return apiFetch(`/ops/demos/${demoId}/deploy`, { method: 'POST', body: JSON.stringify(input) });
}

export async function anonymizeDemo(demoId: string, input: {
  envVarReplacements: Record<string, string>; swapBackendUrl?: string;
  anonymizedBy: string; newSlug?: string; newName?: string; notes?: string;
}): Promise<unknown> {
  return apiFetch(`/ops/demos/${demoId}/anonymize`, { method: 'POST', body: JSON.stringify(input) });
}

export async function extendDemo(demoId: string, input: { additionalDays: number; reason: string }): Promise<unknown> {
  return apiFetch(`/ops/demos/${demoId}/extend`, { method: 'POST', body: JSON.stringify(input) });
}

export async function teardownDemo(demoId: string): Promise<unknown> {
  return apiFetch(`/ops/demos/${demoId}/teardown`, { method: 'POST' });
}

export async function healthCheckDemo(demoId: string): Promise<{ code: number; latencyMs: number; error?: string }> {
  return apiFetch(`/ops/demos/${demoId}/health`, { method: 'POST' });
}

export async function ingestArtifact(input: {
  slug: string;
  name: string;
  customerName: string;
  productId: string;
  artifact: string;
  artifactType: 'jsx' | 'html' | 'vite-zip';
  description?: string;
  ga4MeasurementId?: string;
  tags?: string[];
  ttlDays?: number;
  tenantId?: string;
  engagementId?: string;
  authType?: 'none' | 'password';
  authPassword?: string;
}): Promise<{ demoId: string; slug: string; url: string; status: string; version?: string; message: string; ga4MeasurementId?: string }> {
  return apiFetch('/ops/demos/ingest', { method: 'POST', body: JSON.stringify(input) });
}
